import { useState } from "react";
import DSBox from "../../system-design/DSBox";
import DSCard from "../../system-design/DSCard";
import DSCardContent from "../../system-design/DSCardContent";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSErrorButton from "../../system-design/DSErrorButton";
import DSButton from "../../system-design/DSButton";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import { Column } from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import ConfirmationModal from "../../ConfirmationModal";
import KlydosPickDialog from "../../Gifts/GiftCategories/components/KlydosPickDialog";
import ReorderKlydosDialog from "../../Klydos/components/ReorderKlydosDialog";
import KlydoLoopUrl from "../../../components/klydoPreview/KlydoLoopUrl";
import useGetDeviceFavoriteKlydos, {
  DeviceFavoriteKlydo,
} from "../../Device/hooks/useGetDeviceFavoriteKlydos";
import { hasSound, KlydoType } from "../../../api/klydo";

interface ProfileFavoritesCardProps {
  favorites: string[];
  onChange: (favorites: string[]) => void;
}

/**
 * Informed by DeviceFavoriteCard.tsx, not extracted from it — drops the two
 * bits that only mean something for a single device: the `draftsAllowed`
 * gate (a per-device/per-account permission flag, no profile equivalent —
 * always restrict to KlydoType.Sealed instead) and "Copy IDs"/"Import Other
 * Device Favorites" (both only make sense between two actual devices). The
 * picker, reorder dialog and klydo-hydration hook underneath are already
 * fully generic (take a plain `string[]` of ids despite the device-ish
 * name on the hook) and reused unchanged.
 */
const ProfileFavoritesCard = ({
  favorites,
  onChange,
}: ProfileFavoritesCardProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const [showReorder, setShowReorder] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const { klydos, missingIds, isLoading, hasErrors } =
    useGetDeviceFavoriteKlydos(favorites);

  const columns: Column<DeviceFavoriteKlydo>[] = [
    { field: "position", label: "#", size: 60, sortable: false },
    { field: "loopUrl", label: "Klydo", sortable: false },
    { field: "name", label: "Name", sortable: false },
    { field: "chimesUrls", label: "Chime", sortable: false },
    { field: "capabilitiesV2", label: "Sound", sortable: false },
  ];

  const rowBuilder = (klydo: DeviceFavoriteKlydo) => (
    <>
      <DSTableCell key="position" sx={{ width: 60, fontWeight: 600 }}>
        {klydo.position}
      </DSTableCell>
      <DSTableCell key="klydo">
        <DSBox sx={{ width: 35, height: 35, overflow: "hidden" }}>
          <KlydoLoopUrl loopUrl={klydo?.loopUrl || ""} />
        </DSBox>
      </DSTableCell>
      <DSTableCell key="name">
        {klydo?.name || klydo?.creator || "Unknown"}
      </DSTableCell>
      <DSTableCell key="chime">
        {klydo?.chimesUrls?.length ? "✓" : "✗"}
      </DSTableCell>
      <DSTableCell key="sound">
        {hasSound(klydo?.capabilitiesV2?.animationSound?.mode) ? "✓" : "✗"}
      </DSTableCell>
    </>
  );

  const handleRemove = (klydo: DeviceFavoriteKlydo) => {
    onChange(favorites.filter((id) => id !== klydo.klydoId));
  };

  return (
    <DSCard sx={{ pb: "15px", mb: 2, bgcolor: "background.paper" }}>
      <DSTypography variant="h6" sx={{ px: 2, pt: 2 }}>
        Favorites{favorites.length > 0 ? ` (${favorites.length})` : ""}
      </DSTypography>
      <DSBox
        sx={{ borderBottom: "1px solid", borderColor: "divider", mt: 1 }}
      />
      <DSCardContent>
        <DSTypography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          Rows are in playback order — pushed onto every assigned device's own
          favorites list on Sync.
        </DSTypography>

        {missingIds.length > 0 && (
          <DSTypography
            variant="caption"
            color="error.main"
            sx={{ display: "block", mb: 1 }}
          >
            {`${missingIds.length} favorite(s) not found in the klydo DB and not listed below: ${missingIds.join(", ")}`}
          </DSTypography>
        )}

        {hasErrors ? (
          <DSBox sx={{ textAlign: "center", p: 2, color: "error.main" }}>
            Failed to load favorite klydos
          </DSBox>
        ) : isLoading ? (
          <DSBox sx={{ textAlign: "center", p: 2 }}>
            <DSCircularProgress size={20} />
          </DSBox>
        ) : favorites.length > 0 ? (
          <DSBaseTable
            data={klydos}
            columns={columns}
            rowBuilder={rowBuilder}
            onPressDelete={handleRemove}
            defaultRowsPerPage={5}
            getRowId={(klydo) => klydo.klydoId}
          />
        ) : (
          <DSBox sx={{ textAlign: "center" }}>No favorites added yet</DSBox>
        )}

        <DSBox
          sx={{ display: "flex", gap: "10px", mt: "15px", flexWrap: "wrap" }}
        >
          <DSPrimaryButton onClick={() => setShowPicker(true)}>
            Edit Favorites
          </DSPrimaryButton>
          <DSButton
            disabled={favorites.length < 2}
            onClick={() => setShowReorder(true)}
          >
            Edit Order
          </DSButton>
          {favorites.length > 0 && (
            <DSErrorButton
              onClick={() => setShowClearConfirm(true)}
              style={{ minWidth: "120px" }}
            >
              Clear Favorites
            </DSErrorButton>
          )}
        </DSBox>
      </DSCardContent>

      {showPicker && (
        <KlydosPickDialog
          onClose={() => setShowPicker(false)}
          selectedKlydos={favorites}
          klydoType={KlydoType.Sealed}
          onPickKlydos={(picked) => {
            onChange(picked);
            setShowPicker(false);
          }}
        />
      )}

      {showReorder && (
        <ReorderKlydosDialog
          title="Favorites order"
          description="Assigned devices will play favorites in this order."
          klydosIds={favorites}
          onSave={(ordered) => {
            onChange(ordered);
            setShowReorder(false);
          }}
          onClose={() => setShowReorder(false)}
        />
      )}

      <ConfirmationModal
        open={showClearConfirm}
        message={`Clear all ${favorites.length} favorite(s) from this profile? This profile will no longer manage favorites on sync — assigned devices keep whatever they already have.`}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={() => {
          onChange([]);
          setShowClearConfirm(false);
        }}
      />
    </DSCard>
  );
};

export default ProfileFavoritesCard;
